import React, { lazy, Suspense } from "react";
import { connect } from 'react-redux';
import {
    setDeploymentMessages,
    updateInstanceData,
    updateBodyServer
} from "../../../../../../redux/actions/DeploymentActions";
import { createFetch } from "../../../../../../helpers/fetch/FetchRequest";
import { removeLastModalLayer } from "../../../../../../redux/actions/ModalActions";
import Loading from "../../../../../../components/Loading";
import { adaptServers } from "./dataHelpers/serversAdapter";
import { precessServers } from "./dataHelpers/serversProcessing";
import styles from './Hardware.module.scss'
import showModalDefault from "../../../../../../helpers/ui/ModalDefault";
import { HARDWARE_SLIDERS } from "./components/components/Sliders";
import { CUSTOM_SERVERS_COUNT_DEFAULT, CUSTOM_SERVERS_COUNT_KEY } from "../../../../../../settings/content/deploymentStacker";
import InstanceLayout from "../../layouts/InstanceLayout";
import { API_APP_INTERNAL } from "../../../../../../settings/endpoints/app";

const HardwareSelect = lazy(() => import('./components/HardwareSelect'));

class Hardware extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isMounted: false,
            source: undefined,
        }
    }

    componentDidMount() {
        this.setState({isMounted: true});

        if (!this.props.bodyServer[CUSTOM_SERVERS_COUNT_KEY]) {
            this.props.dispatch(updateBodyServer({[CUSTOM_SERVERS_COUNT_KEY]: CUSTOM_SERVERS_COUNT_DEFAULT}));
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {

        let {isMounted} = this.state;
        let {instanceData, bodyServer, currentCloud, dispatch, requirements} = this.props;
        let {regionsStatus, serversStatus, servers, imagesStatus, images} = instanceData;
        let {
            serversFetch, serversURL, sliderCPU, sliderRAM, sliderStorage
        } = currentCloud.config ? currentCloud.config : {};

        if (isMounted !== prevState.isMounted && !!serversFetch) {
            dispatch(updateInstanceData({regionsStatus: 'loading'}));
            createFetch({
                URL: '/api/instance',
                requestTitle: 'Servers',
                method: 'POST',
                body: {
                    url: !!serversURL ? serversURL : `${API_APP_INTERNAL}/${currentCloud['code']}/servers`,
                },
                onSuccess: response => {
                    let adaptedServers = adaptServers(response.servers, currentCloud['code']);
                    dispatch(updateInstanceData({serversStatus: 'success', servers: adaptedServers}));
                },
                onError: () => {
                    dispatch(updateInstanceData({serversStatus: 'error'}));
                }
            })
        }

        if (
            !!serversFetch
            && (
                serversStatus !== prevProps.instanceData.serversStatus
                || regionsStatus !== prevProps.instanceData.regionsStatus
                || bodyServer.region !== prevProps.bodyServer.region
                || requirements !== prevProps.requirements
                || bodyServer[CUSTOM_SERVERS_COUNT_KEY] !== prevProps.bodyServer[CUSTOM_SERVERS_COUNT_KEY]
            )
            && serversStatus === 'success' && regionsStatus === 'success' && !!bodyServer.region
        ) {

            if (!this.state.source) this.setState({source: servers});

            const processedServers = precessServers(
                this.state.source || servers,
                bodyServer.region,
                requirements,
                this.props.custom ? bodyServer[CUSTOM_SERVERS_COUNT_KEY] : null
            );

            if (!processedServers?.length) {

                const emptyError = 'No hardware data available. Try to choose another region or return later.';

                if (!this.props.messages.some(m => m.message === emptyError)) {
                    dispatch(setDeploymentMessages([...this.props.messages, {
                        message: 'No hardware data available. Try to choose another region or return later.',
                        block: 'instance',
                        target: 'hardware',
                        type: 'error'
                    }]))
                }
                return;
            }

            let selectedServerData = null;

            const priority = 'x86';
            const x86Exists = processedServers.some(s => s.architecture?.toLowerCase() === priority);
            const firstAvailable = processedServers.find(s => {
                return (
                    !s.isLocked
                    && (
                        !!x86Exists && s.architecture.toLowerCase() === priority
                        ||
                        !x86Exists
                    )
                )
            });

            if (!!bodyServer.server) {
                selectedServerData = processedServers.find(s => s.id === bodyServer.server);
                if (!selectedServerData || !!selectedServerData?.isLocked) selectedServerData = firstAvailable;
            } else {
                selectedServerData = firstAvailable;
            }
            // req should be equal to one of allowed slider values to work properly
            const fixToAvailable = (req, type) => {
                // so try to find slider data and fix it
                let fixed = req;
                const slider = HARDWARE_SLIDERS[type];

                let values = [];

                if (slider.values) {
                    values = slider.values;
                } else if (
                    (!!slider.min || slider.min === 0)
                    && (!!slider.max || slider.max === 0)
                    && !!slider.step
                ) {
                    for (let i = slider.min; i <= slider.max; i += slider.step) {
                        values.push(parseFloat(i.toPrecision(12))); // fix 0.1 + 0.2
                    }
                }

                if (!!values?.length) fixed = values.find(v => v >= req);

                return fixed
            }

            let slidersData = {};
            if (sliderCPU) slidersData.cpu = bodyServer.cpu ? bodyServer.cpu : 2;
            if (sliderRAM) slidersData.ram =
                bodyServer.ram && bodyServer.ram >= requirements.ram
                    ? bodyServer.ram
                    : requirements.ram ? fixToAvailable(requirements.ram, 'sliderRAM') : 4;
            if (sliderStorage) slidersData.disk_size =
                bodyServer.disk_size && bodyServer.disk_size >= requirements.storage
                    ? bodyServer.disk_size
                    : requirements.storage ? fixToAvailable(requirements.storage, 'sliderStorage') : 60;

            dispatch(updateBodyServer(
                {...{server: selectedServerData?.id}, ...slidersData})
            );
            dispatch(updateInstanceData({servers: processedServers}));
            dispatch(setDeploymentMessages(this.props.messages.filter(m => m.target !== 'hardware')));
        }
    }

    instanceSettings = (servers, disabled) => {

        const {dispatch} = this.props;

        return (
            <Suspense fallback={<Loading/>}>
                <HardwareSelect
                    custom={this.props.custom}
                    servers={servers}
                    selectServer={server => dispatch(updateBodyServer({server: server}))}
                    selectSSD={bool => dispatch(updateBodyServer({disk_type: bool ? 'pd-ssd' : 'pd-standart'}))}
                    selectSlider={value => dispatch(updateBodyServer(value))}
                    closeModal={() => dispatch(removeLastModalLayer())}
                    disabled={disabled} // @todo
                />
            </Suspense>
        )
    }

    renderHardwareBasics = (selectedServer) => {

        const HARDWARE = ['cpu', 'ram', 'storage'];
        const SLIDERS = {
            cpu: {prop: 'cpu', data: 'sliderCPU', icon: 'fas fa-microchip', unit: null},
            ram: {prop: 'ram', data: 'sliderRAM', icon: 'fas fa-memory', unit: 'Gb'},
            storage: {prop: 'disk_size', data: 'sliderStorage', icon: 'fas fa-hdd', unit: 'Gb'},
        }

        let {bodyServer} = this.props;
        let {config} = this.props.currentCloud;
        let {basic} = selectedServer.adaptedRender;

        return HARDWARE.map((device, index) => {

            const origin = !!basic[device]?.value
                ? basic[device]
                : !!config[SLIDERS[device].data]
                    ? SLIDERS[device]
                    : undefined
            ;

            if (!origin) return null;

            return (
                <div className={styles.hardware__container} key={index}>
                    <i className={`${origin.icon} ${styles.hardware__container__icon}`} aria-hidden="true"/>
                    <div className={styles.hardware__container__value}>
                        {!!basic[device]?.value ? origin.value : bodyServer[origin.prop]}
                        {origin.unit && <span>{origin.unit}</span>}
                    </div>
                </div>
            )
        });
    }

    render() {

        let {instanceData, bodyServer, currentCloud} = this.props;
        let {servers, serversStatus, regionsStatus} = instanceData;
        let selectedServer = !!servers && !!bodyServer.server && servers.find(r => r['id'] === bodyServer['server']);

        if (
            !currentCloud.config
            || (
                !!currentCloud.config?.['serversFetch'] && (serversStatus !== 'success' || !selectedServer)
                || regionsStatus !== 'success'
            )
        ) return (
            <div className={styles['hardware']} id='hardware-anchor'>
                <Loading type='component-cover'/>
            </div>
        );

        let disabled = false;  // @todo after images are done

        return (
            <InstanceLayout
                title={`Hardware${!!selectedServer?.architecture ? ' (' + selectedServer?.architecture + ')' : ''}`}
                id='hardware-anchor'
                disabled={disabled}
                onClick={() => {
                    showModalDefault({
                        component: this.instanceSettings(servers, disabled),
                        title: 'Configure hardware',
                        maxWidth: '1100px',
                        backgroundContainer: ['var(--colorBackgroundContent)']
                    })
                }}
            >
                <div className={styles.hardware}>
                    {this.renderHardwareBasics(selectedServer)}
                </div>
            </InstanceLayout>
        )
    }
}

const mapStateToProps = state => ({
    currentCloud: state.deployment.currentCloud,
    instanceData: state.deployment.instanceData,
    requirements: state.deployment.requirements,
    bodyServer: state.deployment.bodyServer,
    messages: state.deployment.messages,
});

export default connect(mapStateToProps)(Hardware)